import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, ShieldX, Award } from "lucide-react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import { PageHero } from "../../components/Bits";

export default function Verify() {
  const { t } = useApp();
  const [params] = useSearchParams();
  const [number, setNumber] = useState(params.get("no") || params.get("id") || "");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async (num) => {
    if (!num.trim()) return;
    setBusy(true);
    try {
      const r = await api.get(`/public/verify/${encodeURIComponent(num.trim())}`);
      setResult(r.data);
    } catch (e) {
      setResult({ valid: false });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const n = params.get("no") || params.get("id");
    if (n) check(n);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="verify-page">
      <SEO title={t("verify_cert")} description="Verify the authenticity of certificates and ID cards issued by Sahaj Sarathi Foundation." />
      <PageHero title={t("verify_title")} sub={t("verify_sub")} />
      <section className="py-16">
        <div className="container-ssf max-w-xl">
          <form onSubmit={(e) => { e.preventDefault(); check(number); }} className="card-ssf p-8" data-testid="verify-form">
            <label htmlFor="verify-input" className="mb-1.5 block text-sm font-semibold text-navy">{t("cert_no")}</label>
            <div className="flex gap-3">
              <input id="verify-input" data-testid="verify-input" value={number} onChange={(e) => setNumber(e.target.value)}
                placeholder={t("verify_placeholder")}
                className="flex-1 rounded-md border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
              <button type="submit" disabled={busy} className="btn-primary !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="verify-submit">
                {busy ? t("loading") : t("verify_btn")}
              </button>
            </div>
          </form>
          {result && (
            <div className={`card-ssf mt-6 p-8 ${result.valid ? "border-brandgreen/40" : "border-red-200"}`} data-testid="verify-result">
              <div className="flex items-center gap-3">
                {result.valid
                  ? <ShieldCheck className="h-10 w-10 text-brandgreen" strokeWidth={1.5} />
                  : <ShieldX className="h-10 w-10 text-red-500" strokeWidth={1.5} />}
                <h2 className={`font-display text-xl font-bold ${result.valid ? "text-brandgreen" : "text-red-600"}`} data-testid="verify-status">
                  {result.valid ? t("valid") : t("invalid")}
                </h2>
              </div>
              {result.valid && (
                <dl className="mt-6 space-y-3 text-sm">
                  {[["name", result.name], ["type", result.type], ["programme", result.programme], ["issue_date", result.issue_date], ["cert_no", result.cert_no]]
                    .filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 border-b border-gray-100 pb-2">
                      <dt className="font-semibold text-slate-500">{t(k)}</dt>
                      <dd className="font-semibold text-navy">{v}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )}
          <p className="mt-6 flex items-center gap-2 text-xs text-slate-500">
            <Award size={14} className="text-saffron-600" /> Certificates issued by Sahaj Sarathi Foundation carry a QR code linking to this page.
          </p>
        </div>
      </section>
    </div>
  );
}
